<template>
  <div class="eva-page">
    <!-- 进场动画：每次加载都播（不随 state 持久化），结束后落到持久化的阶段 -->
    <IntroSplash v-if="splashVisible" @finish="onSplashFinish" />

    <HomeChoice
      v-else-if="state.stage === 'home'"
      :scope="state.scope"
      :edition="state.edition"
      :has-progress="hasProgress"
      :resume-label="resumeLabel"
      @update:edition="setEdition"
      @select="startEvaluation"
      @continue="resume"
    />

    <RoleSelect
      v-else-if="state.stage === 'role'"
      :scope="state.scope"
      :edition="state.edition"
      :role-id="state.roleId"
      :subject-name="state.subjectName"
      @back="toHome"
      @next="toSelf"
      @update:role-id="(v) => (state.roleId = v)"
      @update:subject-name="(v) => (state.subjectName = v)"
    />

    <SelfAssess
      v-else-if="state.stage === 'self'"
      :self-code="state.selfLevel"
      :subject-name="state.subjectName"
      :scope="state.scope"
      :role-id="state.roleId"
      @back="state.stage = 'role'"
      @next="toWizard"
      @update:self-code="(v) => (state.selfLevel = v)"
    />

    <EvaluationWizard
      v-else-if="state.stage === 'wizard'"
      :indicators="indicators"
      :scores="scores"
      :weights="state.weights"
      :step="state.step"
      :scope="state.scope"
      :edition="state.edition"
      :role-id="state.roleId"
      :subject-name="state.subjectName"
      @back="state.stage = 'self'"
      @finish="toResult"
      @update:step="(v) => (state.step = v)"
      @update-score="applyScore"
      @update:weights="setWeights"
    />

    <exportPDF
      v-else-if="state.stage === 'result'"
      :meta="meta"
      :result="result"
      :indicators="indicators"
      :scores="scores"
      :weights="state.weights"
      @back="state.stage = 'wizard'"
      @restart="restart"
    />
  </div>
</template>

<script setup>
import { computed, reactive, ref, watch, onMounted, nextTick } from 'vue'
import IntroSplash from './IntroSplash.vue'
import HomeChoice from './HomeChoice.vue'
import RoleSelect from './RoleSelect.vue'
import SelfAssess from './SelfAssess.vue'
import EvaluationWizard from './EvaluationWizard.vue'
import exportPDF from './exportPDF.vue'
import { getIndicators, validateData, SCOPES, EDITIONS } from '../data/index.js'
import { evaluate, normalizeWeights } from '../utils/scoring.js'
import {
  loadState, saveState, clearState, setIndicatorScore, getScores, findFirstProgress
} from '../utils/storage.js'

const REQUIRED_STAGES = ['self', 'wizard', 'result']

// 进场动画为瞬时状态，不入 state，保证每次打开/刷新都会播放
const splashVisible = ref(true)

const state = reactive(loadState())

onMounted(() => {
  // 题库自检异常只在开发期提示，不阻断使用
  const errors = validateData()
  if (errors.length) {
    // eslint-disable-next-line no-console
    console.warn('[ma-system] 题库自检异常：', errors)
  }
  // 停留在向导/结果页但缺少前置选择（如清了部分状态）时退回首页
  if (REQUIRED_STAGES.includes(state.stage) && (!state.scope || !state.edition)) {
    state.stage = 'home'
  }
})

// 单一 deep watch 统一落盘，取代旧实现的 15 个 markN watch
watch(state, () => saveState(state), { deep: true })

// 切换主阶段（含生成报告进入结果页）后回到页面顶部，避免停在上一页尾部
watch(
  () => state.stage,
  () => {
    nextTick(() => window.scrollTo({ top: 0, behavior: 'auto' }))
  }
)

const indicators = computed(() =>
  state.scope && state.edition ? getIndicators(state.scope, state.edition) : []
)

const scores = computed(() => getScores(state, state.scope, state.edition))

const resumeTarget = computed(() => findFirstProgress(state))
const hasProgress = computed(() => !!resumeTarget.value)
const resumeLabel = computed(() => {
  const t = resumeTarget.value
  if (!t) return ''
  const s = (SCOPES[t.scope] || {}).name || ''
  const e = (EDITIONS[t.edition] || {}).name || ''
  return [s, e].filter(Boolean).join(' · ')
})

const meta = computed(() => ({
  subjectName: state.subjectName,
  scope: state.scope,
  edition: state.edition,
  roleId: state.roleId,
  selfLevel: state.selfLevel
}))

const result = computed(() =>
  evaluate({ indicators: indicators.value, scores: scores.value, weights: state.weights })
)

function toHome () {
  state.stage = 'home'
}

function onSplashFinish () {
  splashVisible.value = false
  // 首次进入时 stage 缺省为 intro，动画结束后落到首页
  if (state.stage === 'intro') state.stage = 'home'
}

function setEdition (edition) {
  state.edition = edition
  state.step = 0
}

function startEvaluation ({ scope, edition }) {
  state.scope = scope
  state.edition = edition
  state.step = 0
  state.stage = 'role'
}

function resume () {
  // 恢复到真正有进度的那一份评价（可能与当前选择的 scope/edition 不同）
  const t = resumeTarget.value
  if (t) {
    state.scope = t.scope
    state.edition = t.edition
  }
  state.stage = state.scope && state.edition && state.roleId ? 'wizard' : 'role'
}

function toSelf () {
  state.stage = 'self'
}

function toWizard () {
  state.step = 0
  state.stage = 'wizard'
}

function toResult () {
  state.stage = 'result'
}

function applyScore ({ indicatorId, patch }) {
  setIndicatorScore(state, state.scope, state.edition, indicatorId, patch)
}

function setWeights (weights) {
  state.weights = normalizeWeights(weights)
}

function restart () {
  const fresh = clearState()
  Object.assign(state, fresh)
  // clearState 返回 stage:'intro'，但进场动画 splashVisible 此时已为 false，
  // 若直接落到 intro 会既不播动画也不渲染首页 → 空白页。这里显式回到 home。
  state.stage = 'home'
}
</script>

<style scoped>
.eva-page {
  min-height: 100vh;
}
</style>
